import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { getFoundationActuals, refreshFoundationActuals } from "@/lib/api";
import { btn } from "@/components/common/btn";
import { shortDate } from "@/lib/format";

const FINANCE = ["admin", "accounting", "office"];

// Slim footer under the Budget table: the Pull button (Finance / Admin) and when Foundation was last read.
// The posted costs themselves show in the Budget table's Actual column.
export function WOFoundationActualsCard({ woId, jobNo, pushed, onSnapshot }) {
  const { user } = useAuth();
  const canPull = FINANCE.includes((user?.role || "").toLowerCase());
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const take = (data) => { setSnap(data); onSnapshot?.(data); };

  useEffect(() => {
    if (!woId || !pushed) return;
    setErr(null);
    getFoundationActuals(woId).then((r) => take(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Could not load Foundation actuals"));
  }, [woId, pushed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!pushed) return null;
  const refresh = async () => {
    setBusy(true);
    try {
      const r = await refreshFoundationActuals(woId);
      take(r.data); setErr(null);
      toast.success(`Direct costs pulled from Foundation for job ${jobNo}`);
    } catch (e) {
      const d = e?.response?.data?.detail || "Foundation read failed";
      toast[e?.response?.status === 503 ? "warning" : "error"](d, { duration: 8000 });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-1.5" data-testid="wo-foundation-actuals">
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <button type="button" className={btn("data")} onClick={refresh} disabled={busy || !canPull}
          title={canPull ? "Pull this job's posted cost from Foundation now" : "Finance / Admin only"} data-testid="btn-refresh-foundation-actuals">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Pull Direct Costs
        </button>
        <span data-testid="fa-as-of">as of {shortDate(snap?.snapshot_at)}</span>
      </div>
      {err && <div className="text-xs text-rose-700" data-testid="foundation-actuals-error">{err}</div>}
      {snap?.error && !err && <div className="text-xs text-rose-700" data-testid="foundation-actuals-error">Last nightly read failed: {snap.error}</div>}
    </div>
  );
}
